// §11.1 — File Explorer.
//
// It reaches the disk through `HangarAPI.fs` and nothing else, which is the §9.5-scoped gateway:
// being "the file manager" is a row in §9.5's table, not a back door. The two things §11.1 needs
// that the app surface deliberately lacks — the Recycle Bin's restore, and which user's home this
// is — arrive as a `ExplorerShellAccess` capability at composition (D-059).
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type DragEvent as ReactDragEvent,
} from 'react';
import {
  ROOT_APPS,
  USER_FOLDERS,
  basename,
  isDeviceLocal,
  joinVfs,
  mimeForName,
  parentOf,
  type ExplorerShellAccess,
  type FileEntry,
  type HangarAPI,
  type HangarFsApi,
  type TrashItem,
  type VfsPath,
} from '@hangar/shared';
import { COPY } from '@/copy';
import { getClip, setClip, subscribeClip, type FileClip } from './clipboard';
import {
  canMoveInto,
  crumbsFor,
  formatSize,
  formatWhen,
  isHidden,
  selectRange,
  sortEntries,
  summarize,
  type SortKey,
  type ViewMode,
} from './model';
import {
  canCompress,
  compressSelection,
  sendStatus,
  createFile,
  createFolder,
  moveInto,
  paste,
  renameTo,
} from './operations';
import { Peek } from './Peek';
import { VersionHistory } from './History';

/**
 * A drag carrying VFS paths. The payload is read on drop; during `dragover` only `types` is
 * legible, which is exactly why the discriminator is the type and not the data.
 */
const DRAG_TYPE = 'application/x-hangar-paths';

const VIEWS: Array<{ id: ViewMode; label: string }> = [
  { id: 'list', label: COPY.explorer.viewList },
  { id: 'grid', label: COPY.explorer.viewGrid },
  { id: 'column', label: COPY.explorer.viewColumn },
];

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** One directory's contents, kept live by `fs.watch` (§7.1). */
function useDirectory(fs: HangarFsApi, dir: VfsPath): {
  entries: FileEntry[];
  error: string | null;
} {
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let stop: (() => void) | null = null;
    void fs
      .watch(dir, () => {
        // Coalescing is the store's job; a re-list is cheap and always correct.
        if (!cancelled) setVersion((n) => n + 1);
      })
      .then((off) => {
        if (cancelled) off();
        else stop = off;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [fs, dir]);

  useEffect(() => {
    let cancelled = false;
    void fs
      .list(dir)
      .then((listed) => {
        if (cancelled) return;
        setEntries(listed);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setEntries([]);
        setError(messageOf(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [fs, dir, version]);

  return { entries, error };
}

// ── the sidebar tree ─────────────────────────────────────────────────────────────────────────

function TreeNode({
  fs,
  path,
  label,
  depth,
  current,
  onOpen,
  onDropPaths,
}: {
  fs: HangarFsApi;
  path: VfsPath;
  label: string;
  depth: number;
  current: VfsPath;
  onOpen: (path: VfsPath) => void;
  onDropPaths: (paths: VfsPath[], dir: VfsPath) => void;
}) {
  const [open, setOpen] = useState(depth === 0);
  const [children, setChildren] = useState<FileEntry[]>([]);
  const [over, setOver] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void fs
      .list(path)
      .then((listed) => {
        if (!cancelled) setChildren(listed.filter((e) => e.isDir && !isHidden(e.name)));
      })
      .catch(() => {
        if (!cancelled) setChildren([]);
      });
    return () => {
      cancelled = true;
    };
  }, [fs, path, open, current]);

  return (
    <>
      <div
        className="explorer-tree-row"
        data-active={String(current === path)}
        data-over={String(over)}
        style={{ paddingInlineStart: `calc(var(--space-2) + ${depth * 12}px)` }}
        onDragOver={(event) => {
          if (!event.dataTransfer.types.includes(DRAG_TYPE)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          setOver(false);
          const raw = event.dataTransfer.getData(DRAG_TYPE);
          if (!raw) return;
          event.preventDefault();
          onDropPaths(JSON.parse(raw) as VfsPath[], path);
        }}
      >
        <button
          type="button"
          className="explorer-tree-twisty"
          aria-label={open ? `Collapse ${label}` : `Expand ${label}`}
          data-testid={`explorer-tree-twisty-${label}`}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? '▾' : '▸'}
        </button>
        <button
          type="button"
          className="explorer-tree-label"
          data-testid={`explorer-tree-${label}`}
          onClick={() => onOpen(path)}
        >
          {label}
        </button>
      </div>
      {open
        ? children.map((child) => (
            <TreeNode
              key={child.path}
              fs={fs}
              path={child.path}
              label={child.name}
              depth={depth + 1}
              current={current}
              onOpen={onOpen}
              onDropPaths={onDropPaths}
            />
          ))
        : null}
    </>
  );
}

// ── the entry rows ───────────────────────────────────────────────────────────────────────────

interface RowProps {
  entry: FileEntry;
  selected: boolean;
  renaming: boolean;
  cut: boolean;
  view: ViewMode;
  /** §34.6 / D-496 (M60.5) — over this many MB the row is badged "on this device". */
  syncThresholdMb: number;
  onSelect: (entry: FileEntry, event: { ctrl: boolean; shift: boolean }) => void;
  onOpen: (entry: FileEntry) => void;
  onContext: (entry: FileEntry, x: number, y: number) => void;
  onRename: (entry: FileEntry, name: string) => void;
  onCancelRename: () => void;
  onDragPaths: (event: ReactDragEvent, entry: FileEntry) => void;
  onDropPaths: (paths: VfsPath[], dir: VfsPath) => void;
}

function Row({
  entry,
  selected,
  renaming,
  cut,
  view,
  syncThresholdMb,
  onSelect,
  onOpen,
  onContext,
  onRename,
  onCancelRename,
  onDragPaths,
  onDropPaths,
}: RowProps) {
  const [over, setOver] = useState(false);
  const [draft, setDraft] = useState(entry.name);
  const input = useRef<HTMLInputElement | null>(null);
  // Escape unmounts the input, and an unmounting input can still fire `blur` — which would
  // commit the very edit that was just abandoned.
  const abandoned = useRef(false);

  useEffect(() => {
    if (!renaming) return;
    abandoned.current = false;
    setDraft(entry.name);
    const node = input.current;
    if (!node) return;
    node.focus();
    // Select the stem, not the extension — the part anyone actually retypes.
    const dot = entry.name.lastIndexOf('.');
    node.setSelectionRange(0, dot > 0 ? dot : entry.name.length);
  }, [renaming, entry.name]);

  return (
    <div
      className="explorer-entry"
      role="option"
      aria-selected={selected}
      data-kind={entry.isDir ? 'dir' : 'file'}
      data-selected={String(selected)}
      data-cut={String(cut)}
      data-over={String(over)}
      data-view={view}
      data-testid={`explorer-entry-${entry.name}`}
      draggable={!renaming}
      tabIndex={-1}
      onDragStart={(event) => onDragPaths(event, entry)}
      onDragOver={(event) => {
        if (!entry.isDir || !event.dataTransfer.types.includes(DRAG_TYPE)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        setOver(false);
        if (!entry.isDir) return;
        const raw = event.dataTransfer.getData(DRAG_TYPE);
        if (!raw) return;
        event.preventDefault();
        event.stopPropagation();
        onDropPaths(JSON.parse(raw) as VfsPath[], entry.path);
      }}
      onClick={(event) => onSelect(entry, { ctrl: event.ctrlKey || event.metaKey, shift: event.shiftKey })}
      onDoubleClick={() => onOpen(entry)}
      onContextMenu={(event) => {
        event.preventDefault();
        // Without this the pane's own handler runs next and replaces the menu with the
        // background one — which has no Rename on it.
        event.stopPropagation();
        onContext(entry, event.clientX, event.clientY);
      }}
    >
      <span className="explorer-entry-icon" aria-hidden="true">
        {entry.isDir ? '▣' : '▤'}
      </span>
      {renaming ? (
        <input
          ref={input}
          className="explorer-rename"
          data-testid="explorer-rename-input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === 'Enter') onRename(entry, draft);
            if (event.key === 'Escape') {
              abandoned.current = true;
              onCancelRename();
            }
          }}
          onBlur={() => {
            if (!abandoned.current) onRename(entry, draft);
          }}
        />
      ) : (
        <span className="explorer-entry-name">{entry.name}</span>
      )}
      {/*
        §34.6 / D-496 — the file is over the threshold, so sync will never carry it and the row says
        so. `isDeviceLocal` is the *same* function the sync client consults, which is what stops this
        badge appearing on a file sync is happily uploading.

        The span is rendered **always** and empty when it does not apply, rather than conditionally.
        `.explorer-entry[data-view='list']` is a four-column grid, so a fifth child that came and
        went per row would push the size and date columns onto an implicit second row for exactly the
        rows that had a badge. A zero-width `auto` column costs nothing and cannot do that.
      */}
      <span
        className="explorer-entry-device-local"
        data-testid={`explorer-device-local-${entry.name}`}
        data-device-local={String(!entry.isDir && isDeviceLocal(entry.size, syncThresholdMb))}
        title={
          !entry.isDir && isDeviceLocal(entry.size, syncThresholdMb)
            ? COPY.media.deviceLocalTitle(syncThresholdMb)
            : undefined
        }
      >
        {!entry.isDir && isDeviceLocal(entry.size, syncThresholdMb) ? COPY.media.deviceLocal : ''}
      </span>
      {view === 'list' ? (
        <>
          <span className="explorer-entry-size">{entry.isDir ? '' : formatSize(entry.size)}</span>
          <span className="explorer-entry-when">{formatWhen(entry.mtime)}</span>
        </>
      ) : null}
    </div>
  );
}

// ── the Recycle Bin place (§7.8) ─────────────────────────────────────────────────────────────

function TrashView({
  access,
  onNavigate,
  onStatus,
}: {
  access: ExplorerShellAccess;
  onNavigate: (path: VfsPath) => void;
  onStatus: (message: string | null) => void;
}) {
  const [items, setItems] = useState<TrashItem[] | null>(null);

  const reload = useCallback(() => {
    void access.trash
      .list()
      .then(setItems)
      .catch(() => setItems([]));
  }, [access]);

  useEffect(reload, [reload]);

  if (!items) return <div className="explorer-empty" data-testid="explorer-trash-loading" />;

  return (
    <div className="explorer-pane" data-testid="explorer-trash">
      <div className="explorer-toolbar">
        <button
          type="button"
          className="explorer-button"
          data-testid="explorer-trash-empty"
          disabled={items.length === 0}
          onClick={() => {
            void access.trash.empty().then((done) => {
              if (done) reload();
            });
          }}
        >
          {COPY.explorer.emptyBin}
        </button>
      </div>
      <div className="explorer-list" role="listbox" aria-label={COPY.explorer.recycleBin}>
        {items.map((item) => (
          <div
            key={item.key}
            className="explorer-entry"
            data-kind={item.isDir ? 'dir' : 'file'}
            data-view="list"
            data-testid={`explorer-trash-entry-${item.name}`}
          >
            <span className="explorer-entry-icon" aria-hidden="true">
              {item.isDir ? '▣' : '▤'}
            </span>
            <span className="explorer-entry-name">{item.name}</span>
            <span className="explorer-entry-when">
              {COPY.explorer.deletedFrom(parentOf(item.originalPath))}
            </span>
            <button
              type="button"
              className="explorer-button"
              data-testid={`explorer-trash-restore-${item.name}`}
              onClick={() => {
                void access.trash
                  .restore(item.key)
                  .then((entry) => {
                    reload();
                    onNavigate(parentOf(entry.path));
                  })
                  .catch((cause: unknown) => onStatus(messageOf(cause)));
              }}
            >
              {COPY.explorer.restore}
            </button>
            <button
              type="button"
              className="explorer-button"
              data-testid={`explorer-trash-purge-${item.name}`}
              onClick={() => {
                void access.trash.deleteForever(item.key).then((done) => {
                  if (done) reload();
                });
              }}
            >
              {COPY.explorer.deleteForever}
            </button>
          </div>
        ))}
        {items.length === 0 ? (
          <p className="explorer-empty" data-testid="explorer-trash-notice">
            {COPY.explorer.emptyBinNotice}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ── the app ──────────────────────────────────────────────────────────────────────────────────

export function Explorer({ api, access }: { api: HangarAPI; access: ExplorerShellAccess }) {
  const fs = api.fs;
  const home = useMemo(() => access.home(), [access]);
  // §34.6 / D-496 (M60.5) — read per render, so changing the §14 setting re-badges this window.
  const syncThresholdMb = access.syncThresholdMb();

  const [place, setPlace] = useState<'files' | 'trash'>('files');
  const [dir, setDir] = useState<VfsPath>(() => joinVfs(home, 'Documents'));
  const [back, setBack] = useState<VfsPath[]>([]);
  const [forward, setForward] = useState<VfsPath[]>([]);
  const [view, setView] = useState<ViewMode>('list');
  const [sort, setSort] = useState<SortKey>('name');
  const [selection, setSelection] = useState<readonly VfsPath[]>([]);
  const [anchor, setAnchor] = useState<VfsPath | null>(null);
  const [renaming, setRenaming] = useState<VfsPath | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; target: FileEntry | null } | null>(null);
  const [peeking, setPeeking] = useState<VfsPath | null>(null);
  const [historyFor, setHistoryFor] = useState<VfsPath | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const surface = useRef<HTMLDivElement | null>(null);

  const clip = useSyncExternalStore(subscribeClip, getClip, getClip);
  const { entries, error } = useDirectory(fs, dir);

  const visible = useMemo(
    () => sortEntries(entries.filter((entry) => !isHidden(entry.name)), sort),
    [entries, sort],
  );
  const order = useMemo(() => visible.map((entry) => entry.path), [visible]);
  const crumbs = useMemo(() => crumbsFor(dir, home), [dir, home]);
  const peeked = useMemo(
    () => (peeking === null ? null : (visible.find((entry) => entry.path === peeking) ?? null)),
    [peeking, visible],
  );
  const historied = useMemo(
    () => (historyFor === null ? null : (visible.find((entry) => entry.path === historyFor) ?? null)),
    [historyFor, visible],
  );

  // §7.7 — `dragstart` cannot await, so the file a single selection would export is staged now.
  const [dragOut, setDragOut] = useState<string | null>(null);
  useEffect(() => {
    const only = selection.length === 1 ? selection[0] : undefined;
    const entry = only === undefined ? undefined : visible.find((item) => item.path === only);
    if (!entry || entry.isDir) {
      setDragOut(null);
      return;
    }
    let cancelled = false;
    void access
      .prepareDragOut(entry.path, entry.size)
      .then((data) => {
        if (!cancelled) setDragOut(data);
      })
      .catch(() => {
        if (!cancelled) setDragOut(null);
      });
    return () => {
      cancelled = true;
    };
  }, [access, selection, visible]);

  /** §11.1 — `Space` toggles Peek for a single selected file. A directory has nothing to show. */
  const togglePeek = useCallback(() => {
    const only = selection.length === 1 ? selection[0] : undefined;
    if (only === undefined) return;
    const entry = visible.find((candidate) => candidate.path === only);
    if (!entry || entry.isDir) return;
    setPeeking((current) => (current === only ? null : only));
  }, [selection, visible]);

  const labelOf = useCallback(
    (path: VfsPath, fallback: string): string => {
      if (path === home) return COPY.explorer.home;
      if (path === ROOT_APPS) return COPY.explorer.apps;
      return fallback;
    },
    [home],
  );

  const navigate = useCallback(
    (next: VfsPath) => {
      setPlace('files');
      setSelection([]);
      setAnchor(null);
      setMenu(null);
      setPeeking(null);
      if (next === dir) return;
      setBack((stack) => [...stack, dir]);
      setForward([]);
      setDir(next);
    },
    [dir],
  );

  // The pane owns the keyboard (Delete, F2, Ctrl+A/C/X/V), so it has to hold focus from the
  // moment the window opens rather than after the first click.
  useEffect(() => {
    surface.current?.focus();
  }, []);

  // §11.1 — the title bar follows the folder, so a second Explorer window is tellable apart.
  useEffect(() => {
    const title = place === 'trash' ? COPY.explorer.recycleBin : labelOf(dir, basename(dir));
    void api.window.setTitle(title);
  }, [api, dir, place, labelOf]);

  /**
   * A window's `.win-host` is positioned with a transform (§5.2), and a `position: fixed` child
   * of a transformed element is placed against *that* element, not the viewport. So the menu is
   * absolutely positioned inside the app and the pointer's viewport coordinates are converted
   * here — otherwise it lands offset by however far the window has been dragged.
   */
  const openMenu = useCallback((clientX: number, clientY: number, target: FileEntry | null) => {
    const box = surface.current?.getBoundingClientRect();
    setMenu({ x: clientX - (box?.left ?? 0), y: clientY - (box?.top ?? 0), target });
  }, []);

  const select = useCallback(
    (entry: FileEntry, modifiers: { ctrl: boolean; shift: boolean }) => {
      setMenu(null);
      if (modifiers.shift && anchor) {
        setSelection(selectRange(order, anchor, entry.path));
        return;
      }
      if (modifiers.ctrl) {
        setSelection((current) =>
          current.includes(entry.path)
            ? current.filter((path) => path !== entry.path)
            : [...current, entry.path],
        );
        setAnchor(entry.path);
        return;
      }
      setSelection([entry.path]);
      setAnchor(entry.path);
    },
    [anchor, order],
  );

  const run = useCallback((work: Promise<unknown>) => {
    void work.catch((cause: unknown) => setStatus(messageOf(cause)));
  }, []);

  /** §9.2 — a folder is navigated into; a file goes to whichever app the registry names. */
  const open = useCallback(
    (entry: FileEntry, appId?: string) => {
      if (entry.isDir) {
        navigate(entry.path);
        return;
      }
      run(api.launch.openWith(entry.path, appId));
    },
    [api, navigate, run],
  );

  const dropPaths = useCallback(
    (paths: VfsPath[], target: VfsPath) => {
      const movable = paths.filter((path) => canMoveInto(path, target));
      if (movable.length === 0) return;
      run(
        moveInto(fs, movable, target).then((result) => {
          if (result.failed > 0) setStatus(COPY.explorer.pasteFailed(result.failed));
          setSelection([]);
        }),
      );
    },
    [fs, run],
  );

  const startDrag = useCallback(
    (event: ReactDragEvent, entry: FileEntry) => {
      const paths = selection.includes(entry.path) ? [...selection] : [entry.path];
      event.dataTransfer.setData(DRAG_TYPE, JSON.stringify(paths));
      event.dataTransfer.effectAllowed = 'move';
      // §7.7 / D-042 — a drag out of Hangar only carries a file where the browser supports it.
      // Where it does not, the drag stays internal and the context menu's Download is the path;
      // an inert drag handle would be worse than none.
      if (dragOut !== null && paths.length === 1 && paths[0] === entry.path) {
        event.dataTransfer.setData('DownloadURL', dragOut);
        event.dataTransfer.effectAllowed = 'copyMove';
      }
      // §10.2 — and tell the shell, so the same gesture can cross into another window. The shell
      // punches its overlay out around this window, so everything above still works unchanged.
      access.beginContentDrag(paths);
    },
    [access, dragOut, selection],
  );

  /** §7.7 — a host-OS drop. Distinguished from an internal move by the type on the transfer. */
  const importDrop = useCallback(
    (transfer: DataTransfer, into: VfsPath) => {
      run(
        access.importDrop(transfer, into).then((result) => {
          if (result.failed > 0) setStatus(COPY.explorer.importFailed(result.failed));
          else setStatus(COPY.explorer.imported(result.files));
        }),
      );
    },
    [access, run],
  );

  const removeSelected = useCallback(() => {
    if (selection.length === 0) return;
    run(
      Promise.all(selection.map((path) => fs.remove(path))).then(() => {
        setSelection([]);
      }),
    );
  }, [fs, run, selection]);

  const pasteHere = useCallback(
    (clipboard: FileClip | null) => {
      if (!clipboard) return;
      run(
        paste(fs, clipboard, dir).then((result) => {
          if (clipboard.mode === 'cut') setClip(null);
          if (result.failed > 0) setStatus(COPY.explorer.pasteFailed(result.failed));
          setSelection(result.landed);
        }),
      );
    },
    [dir, fs, run],
  );

  const goBack = useCallback(() => {
    const previous = back[back.length - 1];
    if (previous === undefined) return;
    setBack(back.slice(0, -1));
    setForward([dir, ...forward]);
    setDir(previous);
    setSelection([]);
  }, [back, dir, forward]);

  const goForward = useCallback(() => {
    const next = forward[0];
    if (next === undefined) return;
    setBack([...back, dir]);
    setForward(forward.slice(1));
    setDir(next);
    setSelection([]);
  }, [back, dir, forward]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (renaming) return;

      // Peek is modal over the pane: while it is up, the only keys that mean anything are the
      // two that put it away. Delete-behind-the-preview is not a thing anyone wants to discover.
      if (peeking !== null) {
        if (event.key === ' ' || event.key === 'Escape') {
          event.preventDefault();
          setPeeking(null);
        }
        return;
      }

      const accel = event.ctrlKey || event.metaKey;
      if (accel && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        setSelection(order);
        return;
      }
      if (accel && event.key.toLowerCase() === 'c') {
        setClip({ mode: 'copy', paths: selection });
        return;
      }
      if (accel && event.key.toLowerCase() === 'x') {
        setClip({ mode: 'cut', paths: selection });
        return;
      }
      if (accel && event.key.toLowerCase() === 'v') {
        pasteHere(clip);
        return;
      }
      if (event.key === ' ') {
        // Otherwise the pane scrolls, which is the browser's idea of what Space is for.
        event.preventDefault();
        togglePeek();
        return;
      }
      if (event.key === 'Delete') {
        removeSelected();
        return;
      }
      if (event.key === 'F2') {
        const only = selection.length === 1 ? selection[0] : undefined;
        if (only !== undefined) setRenaming(only);
        return;
      }
      if (event.key === 'Enter') {
        const only = visible.find((entry) => entry.path === selection[0]);
        if (only) open(only);
        return;
      }
      if (event.key === 'Backspace') {
        if (dir !== home && dir !== ROOT_APPS) navigate(parentOf(dir));
        return;
      }
      if (event.key === 'Escape') {
        setSelection([]);
        setMenu(null);
      }
    },
    [
      clip,
      dir,
      home,
      navigate,
      open,
      order,
      pasteHere,
      peeking,
      removeSelected,
      renaming,
      selection,
      togglePeek,
      visible,
    ],
  );

  const places: Array<{ label: string; path: VfsPath }> = useMemo(
    () => [
      { label: COPY.explorer.home, path: home },
      ...USER_FOLDERS.map((folder) => ({ label: folder, path: joinVfs(home, folder) })),
      { label: COPY.explorer.apps, path: ROOT_APPS },
    ],
    [home],
  );

  const menuItems = useMemo(() => {
    const target = menu?.target ?? null;
    const items: Array<{ id: string; label: string; run: () => void; enabled: boolean }> = [];
    if (target) {
      const candidates = target.isDir ? [] : access.openWithCandidates(target.path);
      items.push({
        id: 'open',
        label: COPY.explorer.open,
        enabled: target.isDir || candidates.length > 0,
        run: () => open(target),
      });
      // §9.2 — "Open with", straight from the registry. Nothing hard-codes Notepad.
      if (!target.isDir) {
        if (candidates.length === 0) {
          items.push({
            id: 'open-with-none',
            label: `${COPY.explorer.openWith}: ${COPY.explorer.openWithNone}`,
            enabled: false,
            run: () => undefined,
          });
        }
        for (const candidate of candidates) {
          items.push({
            id: `open-with-${candidate.appId}`,
            label: `${COPY.explorer.openWith} ${candidate.name}${candidate.isDefault ? ' ✓' : ''}`,
            enabled: true,
            run: () => open(target, candidate.appId),
          });
        }
        items.push({
          id: 'history',
          label: COPY.explorer.versionHistory,
          enabled: true,
          run: () => setHistoryFor(target.path),
        });
        // §7.7 — the export path that exists in every browser, drag-out or no drag-out (D-042).
        items.push({
          id: 'download',
          label: COPY.explorer.download,
          enabled: true,
          run: () => run(access.download(target.path)),
        });
        // §10.3 — Share hands the *content* to another app, under a §10 token. Distinct from
        // "Open with" above it, which hands over a path and grants the new window the file.
        items.push({
          id: 'share',
          label: COPY.explorer.share,
          enabled: true,
          run: () =>
            run(
              fs.read(target.path).then(async (bytes) => {
                const outcome = await api.transfer.share({
                  items: [{ mime: mimeForName(target.name), bytes, path: target.path }],
                });
                setStatus(outcome === 'delivered' ? COPY.explorer.shared : COPY.explorer.shareCancelled);
              }),
            ),
        });
        // §34.13 (M66.3) — **Send to connection**, the §10.3 send-to family's other member and
        // Explorer's own selection verb, exactly as M56.5's Compress to zip is: the thing being
        // acted on is what is highlighted in this pane, and §6.5 forbids importing another app
        // anyway. What Explorer holds is a capability that opens the shell's roster picker and
        // reports an outcome — it never sees the roster, because who somebody knows is §26.9's
        // business (D-583). Absent for a guest (D-024), not shown and refused.
        if (access.canSendToConnection()) {
          items.push({
            id: 'send-to-connection',
            label: COPY.offers.send,
            enabled: true,
            run: () =>
              run(
                access.sendToConnection(target.path, target.size).then((outcome) => {
                  setStatus(sendStatus(outcome));
                }),
              ),
          });
        }
      }
      items.push({
        id: 'peek',
        label: COPY.explorer.peek,
        enabled: !target.isDir,
        run: () => setPeeking(target.path),
      });
      items.push({
        id: 'cut',
        label: COPY.explorer.cut,
        enabled: true,
        run: () => setClip({ mode: 'cut', paths: selection.length > 0 ? selection : [target.path] }),
      });
      items.push({
        id: 'copy',
        label: COPY.explorer.copy,
        enabled: true,
        run: () => setClip({ mode: 'copy', paths: selection.length > 0 ? selection : [target.path] }),
      });
      items.push({
        id: 'rename',
        label: COPY.explorer.rename,
        enabled: true,
        run: () => setRenaming(target.path),
      });
      items.push({
        id: 'delete',
        label: COPY.explorer.delete,
        enabled: true,
        run: () => {
          if (!selection.includes(target.path)) {
            run(fs.remove(target.path));
            return;
          }
          removeSelected();
        },
      });
    }
    // §34.2 (M56.5) — a selection verb, so it sits with cut/copy rather than in Archive.
    if (canCompress(selection)) {
      items.push({
        id: 'compress',
        label: COPY.archive.compress,
        enabled: true,
        run: () =>
          run(
            compressSelection(fs, dir, selection).then((path) => {
              setSelection([path]);
              setStatus(COPY.archive.compressed(basename(path)));
            }),
          ),
      });
    }
    items.push({
      id: 'paste',
      label: COPY.explorer.paste,
      enabled: clip !== null,
      run: () => pasteHere(clip),
    });
    items.push({
      id: 'new-folder',
      label: COPY.explorer.newFolder,
      enabled: true,
      run: () =>
        run(
          createFolder(fs, dir, COPY.explorer.newFolderName).then((path) => {
            setSelection([path]);
            setRenaming(path);
          }),
        ),
    });
    items.push({
      id: 'new-file',
      label: COPY.explorer.newFile,
      enabled: true,
      run: () =>
        run(
          createFile(fs, dir, COPY.explorer.newFileName).then((path) => {
            setSelection([path]);
            setRenaming(path);
          }),
        ),
    });
    return items;
  }, [access, api, clip, dir, fs, menu, open, pasteHere, removeSelected, run, selection]);

  return (
    <div
      className="explorer"
      data-testid="explorer"
      // §7.7 — whether the current selection has an export staged. Observable because it is a
      // real difference in what a drag will do, and because `dragstart` cannot await (D-082).
      data-dragout={String(dragOut !== null)}
      ref={surface}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onClick={() => setMenu(null)}
    >
      <nav className="explorer-sidebar" aria-label={COPY.explorer.quickAccess} data-testid="explorer-sidebar">
        {places.map((entry) => (
          <button
            key={entry.path}
            type="button"
            className="explorer-place"
            data-testid={`explorer-place-${entry.label}`}
            data-active={String(place === 'files' && dir === entry.path)}
            onClick={() => navigate(entry.path)}
            onDragOver={(event) => {
              if (!event.dataTransfer.types.includes(DRAG_TYPE)) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(event) => {
              const raw = event.dataTransfer.getData(DRAG_TYPE);
              if (!raw) return;
              event.preventDefault();
              dropPaths(JSON.parse(raw) as VfsPath[], entry.path);
            }}
          >
            {entry.label}
          </button>
        ))}
        <button
          type="button"
          className="explorer-place"
          data-testid={`explorer-place-${COPY.explorer.recycleBin}`}
          data-active={String(place === 'trash')}
          onClick={() => {
            setPlace('trash');
            setSelection([]);
          }}
        >
          {COPY.explorer.recycleBin}
        </button>

        <div className="explorer-tree" data-testid="explorer-tree">
          <TreeNode
            fs={fs}
            path={home}
            label={COPY.explorer.home}
            depth={0}
            current={place === 'files' ? dir : '/'}
            onOpen={navigate}
            onDropPaths={dropPaths}
          />
        </div>
      </nav>

      {place === 'trash' ? (
        <TrashView access={access} onNavigate={navigate} onStatus={setStatus} />
      ) : (
        <div className="explorer-pane">
          <div className="explorer-toolbar">
            <button
              type="button"
              className="explorer-button"
              data-testid="explorer-back"
              aria-label={COPY.explorer.back}
              disabled={back.length === 0}
              onClick={goBack}
            >
              ‹
            </button>
            <button
              type="button"
              className="explorer-button"
              data-testid="explorer-forward"
              aria-label={COPY.explorer.forward}
              disabled={forward.length === 0}
              onClick={goForward}
            >
              ›
            </button>
            <button
              type="button"
              className="explorer-button"
              data-testid="explorer-up"
              aria-label={COPY.explorer.up}
              disabled={dir === home || dir === ROOT_APPS}
              onClick={() => navigate(parentOf(dir))}
            >
              ↑
            </button>

            <nav className="explorer-crumbs" aria-label="Location" data-testid="explorer-crumbs">
              {crumbs.map((crumb) => (
                <button
                  key={crumb.path}
                  type="button"
                  className="explorer-crumb"
                  data-testid={`explorer-crumb-${labelOf(crumb.path, crumb.label)}`}
                  data-active={String(crumb.path === dir)}
                  onClick={() => navigate(crumb.path)}
                >
                  {labelOf(crumb.path, crumb.label)}
                </button>
              ))}
            </nav>

            <span className="explorer-spacer" />

            <button
              type="button"
              className="explorer-button"
              data-testid="explorer-new-folder"
              onClick={() =>
                run(
                  createFolder(fs, dir, COPY.explorer.newFolderName).then((path) => {
                    setSelection([path]);
                    setRenaming(path);
                  }),
                )
              }
            >
              {COPY.explorer.newFolder}
            </button>
            <button
              type="button"
              className="explorer-button"
              data-testid="explorer-new-file"
              onClick={() =>
                run(
                  createFile(fs, dir, COPY.explorer.newFileName).then((path) => {
                    setSelection([path]);
                    setRenaming(path);
                  }),
                )
              }
            >
              {COPY.explorer.newFile}
            </button>

            {VIEWS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className="explorer-button"
                data-testid={`explorer-view-${entry.id}`}
                data-active={String(view === entry.id)}
                onClick={() => setView(entry.id)}
              >
                {entry.label}
              </button>
            ))}
          </div>

          {view === 'column' ? (
            <div className="explorer-columns" data-testid="explorer-columns">
              {crumbs.map((crumb, index) => (
                <ColumnPane
                  key={crumb.path}
                  fs={fs}
                  dir={crumb.path}
                  index={index}
                  sort={sort}
                  activeChild={crumbs[index + 1]?.path ?? selection[0] ?? null}
                  onOpen={navigate}
                  onSelect={(entry) => {
                    setSelection([entry.path]);
                    setAnchor(entry.path);
                    if (entry.isDir) navigate(entry.path);
                  }}
                />
              ))}
            </div>
          ) : (
            <div
              className="explorer-list"
              role="listbox"
              aria-multiselectable="true"
              aria-label={labelOf(dir, basename(dir))}
              data-view={view}
              data-testid="explorer-list"
              onContextMenu={(event) => {
                event.preventDefault();
                openMenu(event.clientX, event.clientY, null);
              }}
              onDragOver={(event) => {
                const types = event.dataTransfer.types;
                if (!types.includes(DRAG_TYPE) && !types.includes('Files')) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = types.includes(DRAG_TYPE) ? 'move' : 'copy';
              }}
              onDrop={(event) => {
                const raw = event.dataTransfer.getData(DRAG_TYPE);
                if (raw) {
                  event.preventDefault();
                  dropPaths(JSON.parse(raw) as VfsPath[], dir);
                  return;
                }
                // §7.7 — a drop from the host OS. Not an internal move: it carries files.
                if (event.dataTransfer.types.includes('Files')) {
                  event.preventDefault();
                  importDrop(event.dataTransfer, dir);
                }
              }}
            >
              {view === 'list' ? (
                <div className="explorer-head">
                  {(
                    [
                      ['name', COPY.explorer.columnName],
                      ['size', COPY.explorer.columnSize],
                      ['modified', COPY.explorer.columnModified],
                    ] as Array<[SortKey, string]>
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className="explorer-head-cell"
                      data-testid={`explorer-sort-${key}`}
                      data-active={String(sort === key)}
                      onClick={() => setSort(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}

              {visible.map((entry) => (
                <Row
                  key={entry.path}
                  entry={entry}
                  view={view}
                  syncThresholdMb={syncThresholdMb}
                  selected={selection.includes(entry.path)}
                  renaming={renaming === entry.path}
                  cut={clip?.mode === 'cut' && clip.paths.includes(entry.path)}
                  onSelect={select}
                  onOpen={open}
                  onContext={(target, x, y) => {
                    if (!selection.includes(target.path)) setSelection([target.path]);
                    openMenu(x, y, target);
                  }}
                  onRename={(target, name) => {
                    setRenaming(null);
                    run(renameTo(fs, target.path, name).then((path) => setSelection([path])));
                  }}
                  onCancelRename={() => setRenaming(null)}
                  onDragPaths={startDrag}
                  onDropPaths={dropPaths}
                />
              ))}

              {visible.length === 0 && !error ? (
                <p className="explorer-empty" data-testid="explorer-empty">
                  {COPY.explorer.emptyFolder}
                </p>
              ) : null}
              {error ? (
                <p className="explorer-empty" data-testid="explorer-error">
                  {error}
                </p>
              ) : null}
            </div>
          )}

          {historied ? (
            <VersionHistory
              fs={fs}
              access={access}
              api={api}
              entry={historied}
              onClose={() => setHistoryFor(null)}
            />
          ) : null}

          <footer className="explorer-status" data-testid="explorer-status">
            {status ?? summarize(visible, selection.length)}
          </footer>
        </div>
      )}

      {peeked ? <Peek fs={fs} entry={peeked} onClose={() => setPeeking(null)} /> : null}

      {menu ? (
        <div
          className="explorer-menu"
          role="menu"
          data-testid="explorer-menu"
          style={{ insetInlineStart: menu.x, insetBlockStart: menu.y }}
          onClick={(event) => event.stopPropagation()}
        >
          {menuItems.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              className="explorer-menu-item"
              data-testid={`explorer-menu-${item.id}`}
              disabled={!item.enabled}
              onClick={() => {
                setMenu(null);
                item.run();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** One column of the miller view (§11.1). Each keeps its own listing, live like the main pane. */
function ColumnPane({
  fs,
  dir,
  index,
  sort,
  activeChild,
  onOpen,
  onSelect,
}: {
  fs: HangarFsApi;
  dir: VfsPath;
  index: number;
  sort: SortKey;
  activeChild: VfsPath | null;
  onOpen: (path: VfsPath) => void;
  onSelect: (entry: FileEntry) => void;
}) {
  const { entries } = useDirectory(fs, dir);
  const visible = useMemo(
    () => sortEntries(entries.filter((entry) => !isHidden(entry.name)), sort),
    [entries, sort],
  );

  return (
    <div className="explorer-column" data-testid={`explorer-column-${index}`}>
      {visible.map((entry) => (
        <button
          key={entry.path}
          type="button"
          className="explorer-column-entry"
          data-kind={entry.isDir ? 'dir' : 'file'}
          data-active={String(entry.path === activeChild)}
          data-testid={`explorer-column-${index}-${entry.name}`}
          onClick={() => onSelect(entry)}
          onDoubleClick={() => {
            if (entry.isDir) onOpen(entry.path);
          }}
        >
          <span className="explorer-entry-name">{entry.name}</span>
          {entry.isDir ? <span aria-hidden="true">›</span> : null}
        </button>
      ))}
    </div>
  );
}
